// Country / province-state dropdown options shared by the Add Location wizard
// (AddressStep) and the Edit Location Details dialog. Values are the codes the
// backend persists on the warehouse (ISO-3166 alpha-2 country, 2-letter
// province/state).

export interface AddressOption {
  value: string
  label: string
}

export const COUNTRY_OPTIONS: AddressOption[] = [
  { value: 'CA', label: 'Canada' },
  { value: 'US', label: 'United States' },
]

export const STATE_OPTIONS: Record<string, AddressOption[]> = {
  CA: [
    { value: 'AB', label: 'Alberta' },
    { value: 'BC', label: 'British Columbia' },
    { value: 'MB', label: 'Manitoba' },
    { value: 'NB', label: 'New Brunswick' },
    { value: 'NL', label: 'Newfoundland and Labrador' },
    { value: 'NS', label: 'Nova Scotia' },
    { value: 'NT', label: 'Northwest Territories' },
    { value: 'NU', label: 'Nunavut' },
    { value: 'ON', label: 'Ontario' },
    { value: 'PE', label: 'Prince Edward Island' },
    { value: 'QC', label: 'Quebec' },
    { value: 'SK', label: 'Saskatchewan' },
    { value: 'YT', label: 'Yukon' },
  ],
  US: [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' },
  ],
}

// Free-text country names seen on legacy warehouse records.
const COUNTRY_ALIASES: Record<string, string> = {
  canada: 'CA',
  'united states': 'US',
  'united states (us)': 'US',
  'united states of america': 'US',
  usa: 'US',
  us: 'US',
  ca: 'CA',
}

/**
 * Map a stored country (code or legacy free-text name) to a supported option
 * code. Unknown values are returned trimmed but otherwise untouched so the
 * caller can still surface them instead of silently wiping the field.
 */
export const normalizeCountry = (raw?: string | null): string => {
  const trimmed = (raw ?? '').trim()
  if (!trimmed) return ''
  return COUNTRY_ALIASES[trimmed.toLowerCase()] ?? trimmed
}

/**
 * Map a stored province/state (code or full name, any case) to the option
 * code for the given country. Unknown values are returned trimmed.
 */
export const normalizeState = (
  country: string,
  raw?: string | null,
): string => {
  const trimmed = (raw ?? '').trim()
  if (!trimmed) return ''
  const needle = trimmed.toLowerCase()
  const match = (STATE_OPTIONS[country] ?? []).find(
    (o) => o.value.toLowerCase() === needle || o.label.toLowerCase() === needle,
  )
  return match?.value ?? trimmed
}

export const getStateFieldLabel = (country: string): string => {
  if (country === 'CA') return 'Province'
  if (country === 'US') return 'State'
  return 'Province / State'
}

/** Display name for a stored country; falls back to the raw value. */
export const getCountryDisplayName = (raw?: string | null): string => {
  const code = normalizeCountry(raw)
  return COUNTRY_OPTIONS.find((o) => o.value === code)?.label ?? code
}

/** Display name for a stored province/state; falls back to the raw value. */
export const getStateDisplayName = (
  country?: string | null,
  raw?: string | null,
): string => {
  const countryCode = normalizeCountry(country)
  const code = normalizeState(countryCode, raw)
  return (
    (STATE_OPTIONS[countryCode] ?? []).find((o) => o.value === code)?.label ??
    code
  )
}
